import { RefObject, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { unwrapResult } from '@reduxjs/toolkit';
import { useHistory } from 'react-router-dom';
import { SerialCertificationApiRequestParam } from '../types/apis/serialCertificationApi';
import useAccessKey from './useAccessKey';
import useCheckTransition from './useCheckTransition';
import { PAGE_PATH_NAME } from '../utils/const';

const ERR_MSG = {
  WARN_INPUT_PARAM:
    'シリアルはアルファベット大文字5文字、数字16文字で入力してください。',
  INFO_INVALID_SERIAL:
    'シリアル認証に失敗しました。入力されたシリアルに誤りがあります。',
  ERR_UNKNOWN: '認証時に不明なエラーが発生しました。',
} as const;
type ERR_MSG = typeof ERR_MSG[keyof typeof ERR_MSG];

// Ref類
export type InputSerialRef = {
  serialHeadRef: RefObject<HTMLInputElement>;
  serialNumFirstRef: RefObject<HTMLInputElement>;
  serialNumSecondRef: RefObject<HTMLInputElement>;
  serialNumThirdRef: RefObject<HTMLInputElement>;
  serialNumFourthRef: RefObject<HTMLInputElement>;
};

export type UseSerialCetificationValue = {
  subDirectory: string; // サブディレクトリ
  inputSerialRef: InputSerialRef; // Ref類
  isCallingApi: boolean; // APIコール中かどうか
  onClickNext: () => void; // [次へ進む]ボタン押下時処理
  message: ERR_MSG | null;
};

const useSerialCetification = (): UseSerialCetificationValue => {
  const { callSerialCetificationAPI } = useAccessKey();

  // state
  // エラーメッセージ
  const [message, setMessage] = useState<ERR_MSG | null>(null);
  // サブディレクトリ
  const [subDirectory, setSubDirectory] = useState<string>('');
  // APIコール中かどうか
  const [isCallingApi, setIsCallingApi] = useState<boolean>(false);

  // Ref
  // シリアルヘッド(アルファベット5桁)のRef
  const serialHeadRef = useRef<HTMLInputElement>(null);
  // シリアル番号の1～4桁目のRef
  const serialNumFirstRef = useRef<HTMLInputElement>(null);
  // シリアル番号の5～8桁目のRef
  const serialNumSecondRef = useRef<HTMLInputElement>(null);
  // シリアル番号の9～12桁目のRef
  const serialNumThirdRef = useRef<HTMLInputElement>(null);
  // シリアル番号の13～16桁目のRef
  const serialNumFourthRef = useRef<HTMLInputElement>(null);

  // history
  const history = useHistory();

  // ログイン済みチェック処理
  const { allowTransition } = useCheckTransition();

  // 初期処理
  useEffect(() => {
    // ログイン済みチェック処理
    if (allowTransition()) {
      // ログイン済みチェック処理がtureの場合、トレーニング選択画面に遷移するためreturn
      return;
    }

    // サブディレクトリの取得
    const path = window.location.pathname.split('/');
    if (path !== undefined) {
      setSubDirectory(path[1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 次へ進むボタン押下時処理
   */
  const onClickNext = () => {
    const serialHead = serialHeadRef.current?.value ?? '';
    const first = serialNumFirstRef.current?.value ?? '';
    const second = serialNumSecondRef.current?.value ?? '';
    const third = serialNumThirdRef.current?.value ?? '';
    const fourth = serialNumFourthRef.current?.value ?? '';
    const serialNumber = `${serialHead.toUpperCase()}-${first}${second}${third}${fourth}`;

    // [次へ進む]ボタン押下でエラー文言を非表示にする
    setMessage(null);

    // インジケータ表示
    setIsCallingApi(true);

    // デバイスID(uuid)の発行
    const deviceID = uuidv4();

    // シリアル認証API呼び出し
    const param: SerialCertificationApiRequestParam = {
      serial: serialNumber,
      deviceId: deviceID,
      courseId: subDirectory,
    };

    callSerialCetificationAPI(param)
      .then(unwrapResult)
      .then((res) => {
        // finallyでstate変更すると、result_codeがOKの場合に、
        // 画面遷移後にstateを変更してしまいコンソールでエラーが出るため、ここでインジケータ解除する。
        setIsCallingApi(false);

        if (res.resultCode === 'OK') {
          // 画面遷移
          history.push(PAGE_PATH_NAME.LESSON);
        } else if (res.resultCode === 'WARN_INPUT_PARAM') {
          throw ERR_MSG.WARN_INPUT_PARAM;
        } else if (res.resultCode === 'INFO_INVALID_SERIAL') {
          throw ERR_MSG.INFO_INVALID_SERIAL;
        } else {
          throw ERR_MSG.ERR_UNKNOWN;
        }
      })
      .catch((err) => {
        // finallyでstate変更すると、result_codeがOKの場合に、
        // 画面遷移後にstateを変更してしまいコンソールでエラーが出るため、ここでインジケータ解除する。
        setIsCallingApi(false);

        // エラーラベル表示
        if (err === ERR_MSG.WARN_INPUT_PARAM) {
          setMessage(ERR_MSG.WARN_INPUT_PARAM);
        } else if (err === ERR_MSG.INFO_INVALID_SERIAL) {
          setMessage(ERR_MSG.INFO_INVALID_SERIAL);
        } else {
          setMessage(ERR_MSG.ERR_UNKNOWN);
        }
      });
  };

  return {
    subDirectory,
    inputSerialRef: {
      serialHeadRef,
      serialNumFirstRef,
      serialNumSecondRef,
      serialNumThirdRef,
      serialNumFourthRef,
    },
    isCallingApi,
    onClickNext,
    message,
  };
};
export type { ERR_MSG };
export default useSerialCetification;
